import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Row,
    Col,
    Button,
    Space,
    Modal,
    DatePicker,
} from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';

import { fetchWoodData } from '../../../../redux/slice/masterSlice';


interface WoodModalProps {
    visible: boolean;
    onCancel: () => void;
    onCreate: (data: any) => void; // Callback after successful submit
    initialValues?: {
        wood_id?: number;
        wood_name?: string;
        wood_thickness?: number | string;
        wood_length?: number | string;
        wood_lenth?: number | string;
        wood_qty?: number | string;
        qty?: number | string;
    } | null;
    isEditMode?: boolean;
}
const WoodModal: React.FC<WoodModalProps> = ({
    visible,
    onCancel,
    onCreate,
    initialValues = null,
    isEditMode = false,
}) => {

    const dispatch = useAppDispatch();
    const [form] = Form.useForm();
    const [isModalClose, setIsModalClose] = useState(true);

    // Determine if submit button should be disabled based on form validation
    const isSubmitDisabled = () => {
        const errors = form.getFieldsError();
        return errors.some(({ errors }) => errors.length > 0);
    };


    // Handle form submit
    const handleAddwood = async (values: any) => {
        try {
            const dataVal = {
                wood_name: values.wood_name,
                wood_thickness: values.wood_thickness,
                wood_length: values.wood_length,
                wood_lenth: values.wood_length,
                wood_qty: values.wood_qty,
                qty: values.wood_qty,
                ...(initialValues?.wood_id && { wood_id: initialValues.wood_id }),
            };
            const url = isEditMode
                ? 'edit-wood'
                : 'add-wood';

            const method = isEditMode ? 'put' : 'post';

            const data = await apiRequest({
                method,
                url,
                data: dataVal,
            });
            console.log(dataVal);

            onCreate(data); // Notify parent about success
            showSuccess(
                isEditMode ? 'Wood updated successfully!' : 'Wood created successfully!',
            );
            form.resetFields();

            if (isModalClose) {
                onCancel(); // Close modal only if Submit button was clicked (not Submit & Create New)
            }
            dispatch(fetchWoodData());
        } catch (error) {
            showError(`Failed to ${isEditMode ? 'update' : 'create'} Wood`);
        }
    };

    // When modal opens or mode changes, set/reset form fields accordingly
    useEffect(() => {
        if (visible && isEditMode && initialValues) {
            form.setFieldsValue({
                wood_name: initialValues.wood_name,
                wood_thickness: initialValues.wood_thickness,
                wood_length: initialValues.wood_length ?? initialValues.wood_lenth,
                wood_qty: initialValues.wood_qty ?? initialValues.qty,
            });
        } else if (visible) {
            form.resetFields();
        }
    }, [visible, isEditMode, initialValues, form]);




    return (
        <Modal
            title={isEditMode ? 'Edit Wood' : 'Add New Wood'}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            width="90%"
            destroyOnHidden
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleAddwood}
                autoComplete="off"
            >
                <Row gutter={16}>
                    {/* Wood Name */}
                    <Col span={12}>
                        <Form.Item
                            name="wood_name"
                            label="Wood Name"
                            rules={[
                                { required: true, message: 'Please enter the Wood Name' },
                                { max: 50, message: 'Maximum 50 characters allowed' },
                            ]}
                        >
                            <Input maxLength={50} placeholder="e.g., African Mahogany" />
                        </Form.Item>
                    </Col>

                    {/* Wood Thickness */}
                    <Col span={12}>
                        <Form.Item
                            name="wood_thickness"
                            label="Thickness (In Inch)"
                            rules={[{ required: true, message: 'Please enter wood thickness' }]}
                        >
                            <Input placeholder="e.g., 5" />
                        </Form.Item>
                    </Col>

                    {/* Wood Length */}
                    <Col span={12}>
                        <Form.Item
                            name="wood_length"
                            label="Length (In Inch)"
                            rules={[{ required: true, message: 'Please enter wood length' }]}
                        >
                            <Input placeholder="e.g., 10" />
                        </Form.Item>
                    </Col>

                    {/* Wood Qty */}
                    <Col span={12}>
                        <Form.Item
                            name="wood_qty"
                            label="Qty"
                            rules={[{ required: true, message: 'Please enter quantity' }]}
                        >
                            <Input type="number" min={0} placeholder="e.g., 100" />
                        </Form.Item>
                    </Col>

                </Row>


                <Form.Item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Space>
                        <Button onClick={onCancel}>Cancel</Button>
                        {!isEditMode && <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(false)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit & Create New Wood
                        </Button>}
                        <Button
                            type="primary"
                            htmlType="submit"
                            onClick={() => setIsModalClose(true)}
                            disabled={isSubmitDisabled()}
                        >
                            Submit
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default WoodModal;
